/**
 * What a call costs.
 *
 * Every provider call in the server records what it consumed — tokens in,
 * tokens out, images made — and this file turns that into money. The two are
 * kept apart on purpose: usage is a fact the API reports and never changes,
 * while a price is a number on a web page that does. Re-costing a month of
 * history after a price change is a matter of editing this table.
 *
 * Each price carries where it came from and when it was last checked. Prices
 * marked `verified: false` were read from third-party aggregators because the
 * provider's own page could not be reached from where this was written; they
 * are close enough to rank options by, and wrong enough that a bill should be
 * reconciled against the real one before anyone quotes a margin.
 *
 * `PRICE_OVERRIDES` (JSON, keyed by model) corrects any entry without a
 * deploy: `{"gemini-3-pro-image-preview":{"perImage":{"1K":0.12}}}`.
 */
export const emptyUsage = () => ({
    inputTokens: 0,
    outputTokens: 0,
    cachedInputTokens: 0,
    cacheWriteTokens: 0,
    images: {},
    imageOutputTokens: 0,
});
export function addUsage(into, more) {
    into.inputTokens += more.inputTokens ?? 0;
    into.outputTokens += more.outputTokens ?? 0;
    into.cachedInputTokens += more.cachedInputTokens ?? 0;
    into.cacheWriteTokens += more.cacheWriteTokens ?? 0;
    into.imageOutputTokens += more.imageOutputTokens ?? 0;
    for (const [tier, n] of Object.entries(more.images ?? {})) {
        into.images[tier] = (into.images[tier] ?? 0) + (n ?? 0);
    }
    return into;
}
/**
 * The table. Ordered by how much of a shoot's bill each line usually is.
 */
export const PRICES = {
    /* ------------------------------ renderers ----------------------------- */
    'gemini-3-pro-image-preview': {
        inputPerM: 2,
        outputPerM: 12,
        imageOutputPerM: 120,
        perImage: { '1K': 0.134, '2K': 0.134, '4K': 0.24, std: 0.134 },
        source: 'openrouter.ai/google/gemini-3-pro-image-preview; pricepertoken.com',
        checkedOn: '2026-09-06',
        verified: false,
        note: 'ai.google.dev was unreachable when this was written. Two aggregators agree on these; ' +
            'confirm against the Gemini API pricing page before quoting a margin.',
    },
    'gemini-3.1-flash-image-preview': {
        inputPerM: 0.5,
        outputPerM: 3,
        perImage: { '1K': 0.067, '2K': 0.101, '4K': 0.151, std: 0.067 },
        source: 'aggregators (developer.puter.com, aifreeapi.com); 2K interpolated',
        checkedOn: '2026-10-09',
        verified: false,
        note: 'Nano Banana 2. The economy renderer for rolls of poses, at 1K. Confirm on the Gemini API pricing page.',
    },
    'gemini-2.5-flash-image': {
        inputPerM: 0.3,
        outputPerM: 2.5,
        imageOutputPerM: 30,
        perImage: { std: 0.039, '1K': 0.039 },
        source: 'aggregators (same as above)',
        checkedOn: '2026-09-06',
        verified: false,
        note: 'The cheap renderer. Worth benchmarking against the Pro model per frame, not per list price.',
    },
    'gpt-image-1': {
        inputPerM: 5,
        outputPerM: 40,
        perImage: { std: 0.04, '1K': 0.04, '2K': 0.17 },
        source: 'unverified — set PRICE_OVERRIDES from platform.openai.com/docs/pricing',
        checkedOn: '2026-09-06',
        verified: false,
        note: 'OpenAI bills gpt-image-1 by output image tokens that vary with quality; per-image figures are the medium-quality points.',
    },
    /* ----------------------------- text agents ---------------------------- */
    'claude-sonnet-5': {
        inputPerM: 2,
        outputPerM: 10,
        cachedInputPerM: 0.2,
        cacheWritePerM: 2.5,
        source: 'platform.claude.com/docs/en/about-claude/pricing',
        checkedOn: '2026-09-06',
        verified: true,
    },
    // The undated id is the canonical one; the dated one is the same model.
    'claude-haiku-4-5': {
        inputPerM: 1,
        outputPerM: 5,
        cachedInputPerM: 0.1,
        cacheWritePerM: 1.25,
        source: 'platform.claude.com/docs/en/about-claude/pricing',
        checkedOn: '2026-09-06',
        verified: true,
    },
    'claude-haiku-4-5-20251001': {
        inputPerM: 1,
        outputPerM: 5,
        cachedInputPerM: 0.1,
        cacheWritePerM: 1.25,
        source: 'platform.claude.com/docs/en/about-claude/pricing',
        checkedOn: '2026-09-06',
        verified: true,
        note: 'Candidate for the mechanical agents (per-POV outfit tailoring) where Sonnet is more model than the job needs.',
    },
    'claude-opus-5': {
        inputPerM: 5,
        outputPerM: 25,
        cachedInputPerM: 0.5,
        cacheWritePerM: 6.25,
        source: 'platform.claude.com/docs/en/about-claude/pricing',
        checkedOn: '2026-09-06',
        verified: true,
    },
    /* -------------------------------- mocks ------------------------------- */
    'mock-renderer': {
        inputPerM: 0,
        outputPerM: 0,
        perImage: { std: 0 },
        source: 'free',
        checkedOn: '—',
        verified: true,
    },
    'mock-reasoner': { inputPerM: 0, outputPerM: 0, source: 'free', checkedOn: '—', verified: true },
    'mock-vision': { inputPerM: 0, outputPerM: 0, source: 'free', checkedOn: '—', verified: true },
};
/** Applies `PRICE_OVERRIDES`, deeply, without touching the table itself. */
export function priceFor(model, overrides = {}) {
    const base = PRICES[model];
    const over = overrides[model];
    if (!base && !over)
        return undefined;
    return {
        ...(base ?? { inputPerM: 0, outputPerM: 0, source: 'override', checkedOn: '—', verified: false }),
        ...over,
        perImage: { ...(base?.perImage ?? {}), ...(over?.perImage ?? {}) },
    };
}
/**
 * Cost of one call, in micro-dollars — an integer, so a million tiny calls
 * summed in a database do not drift the way floats do.
 *
 * Image cost is taken from image output tokens when the provider reported
 * them, and from the per-image tier otherwise. Never both.
 */
export function costMicros(model, usage, overrides) {
    const price = priceFor(model, overrides);
    if (!price)
        return 0;
    const perToken = (n, perM) => (n * perM) / 1_000_000;
    let usd = perToken(usage.inputTokens, price.inputPerM) +
        perToken(usage.outputTokens, price.outputPerM) +
        perToken(usage.cachedInputTokens, price.cachedInputPerM ?? price.inputPerM / 10) +
        perToken(usage.cacheWriteTokens, price.cacheWritePerM ?? price.inputPerM * 1.25);
    if (usage.imageOutputTokens > 0 && price.imageOutputPerM) {
        usd += perToken(usage.imageOutputTokens, price.imageOutputPerM);
    }
    else {
        for (const [tier, n] of Object.entries(usage.images)) {
            const each = price.perImage?.[tier] ?? price.perImage?.std ?? 0;
            usd += (n ?? 0) * each;
        }
    }
    return Math.round(usd * 1_000_000);
}
export const microsToUsd = (micros) => `$${(micros / 1_000_000).toFixed(4)}`;
/** Whether any price that went into a total is one nobody has confirmed. */
export function hasUnverifiedPrice(models, overrides) {
    for (const model of models) {
        const price = priceFor(model, overrides);
        if (price && !price.verified)
            return true;
    }
    return false;
}
/**
 * Money, written the way the reader's language writes it.
 *
 * The providers bill in dollars, so the currency is always passed in and
 * never assumed or hard-coded at the call site.
 */
export function formatMoney(minorUnits, currency, locale = 'en-GB', fractionDigits = 2) {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(minorUnits / 100);
}
/** The same, from micro-units rather than minor units. */
export const formatMicros = (micros, currency, locale = 'en-GB', fractionDigits = 2) => formatMoney(micros / 10_000, currency, locale, fractionDigits);
/**
 * What one delivered frame costs to make, in micro-dollars: one render, and
 * nothing behind it. The stronger renderer at 2K is about $0.134 a frame; the
 * newer Flash model at 1K, a roll set to economy, about $0.067. An estimate
 * shown before a roll runs, so you know what it will cost on your own keys —
 * the ledger records what was actually spent.
 */
export const FRAME_COST_MICROS = {
    economy: 70_000,
    strong: 140_000,
};
/**
 * What a roll should cost before anything is rendered, whichever way it was
 * set up: one message to the image model per photograph — the reference of
 * each place with everything in it, every other frame the next message of its
 * conversation.
 */
export const estimatePoseRollMicros = (frames, settings = {}) => Math.max(0, Math.round(frames)) *
    (settings.fidelity === 'standard' ? FRAME_COST_MICROS.economy : FRAME_COST_MICROS.strong);
/** The same, for a roll composed, recreated or briefed. */
export const estimateMicros = estimatePoseRollMicros;
//# sourceMappingURL=pricing.js.map